import { useCallback, useState } from 'react';

import { logger } from '../telemetry';

export interface SubmissionStateBase<TStatus extends string> {
  status: TStatus;
  errorMessage: string | null;
}

export interface SubmissionPhaseError<TStatus extends string> {
  status: TStatus;
  message: string;
  operation: string;
  logMessage: string;
}

export interface RunSubmissionPhaseConfig<TState extends SubmissionStateBase<string>, TResult> {
  pendingStatus: TState['status'];
  execute: () => Promise<TResult>;
  getSuccessPatch?: (result: TResult) => Partial<TState>;
  getError: (error: unknown) => SubmissionPhaseError<TState['status']>;
}

export interface UseSubmissionOrchestratorOptions<TState extends SubmissionStateBase<string>> {
  initialState: TState;
  feature: string;
  submittingStatuses: ReadonlySet<TState['status']>;
  isDraftLocked: (state: TState) => boolean;
}

export interface UseSubmissionOrchestratorResult<TState extends SubmissionStateBase<string>> {
  state: TState;
  isSubmitting: boolean;
  isDraftLocked: boolean;
  runPhase: <TResult>(config: RunSubmissionPhaseConfig<TState, TResult>) => Promise<TResult | null>;
  patchState: (patch: Partial<TState>) => void;
  reset: () => void;
}

export function useSubmissionOrchestrator<TState extends SubmissionStateBase<string>>(
  options: UseSubmissionOrchestratorOptions<TState>
): UseSubmissionOrchestratorResult<TState> {
  const { initialState, feature, submittingStatuses, isDraftLocked } = options;
  const [state, setState] = useState<TState>(initialState);

  const runPhase = useCallback(
    async <TResult>(config: RunSubmissionPhaseConfig<TState, TResult>): Promise<TResult | null> => {
      setState((current) => ({ ...current, status: config.pendingStatus, errorMessage: null }));

      try {
        const result = await config.execute();
        if (config.getSuccessPatch) {
          const patch = config.getSuccessPatch(result);
          setState((current) => ({ ...current, ...patch }));
        }
        return result;
      } catch (error) {
        const errorDetails = config.getError(error);
        logger.error(
          errorDetails.logMessage,
          {
            surface: 'handled',
            feature,
            operation: errorDetails.operation,
            status: errorDetails.status,
            errorMessage: errorDetails.message,
          },
          error
        );
        setState((current) => ({
          ...current,
          status: errorDetails.status,
          errorMessage: errorDetails.message,
        }));
        return null;
      }
    },
    [feature]
  );

  const patchState = useCallback((patch: Partial<TState>) => {
    setState((current) => ({ ...current, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, [initialState]);

  return {
    state,
    isSubmitting: submittingStatuses.has(state.status),
    isDraftLocked: isDraftLocked(state),
    runPhase,
    patchState,
    reset,
  };
}
